import * as React from 'react';

import * as api from '@gitbook/api-client';
import { createPalette, PaletteAtomButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    type ButtonProps,
    DialogRoot,
    SidebarEmptyState,
    Tooltip,
    useDialogTriggerState,
} from '@gitbook/spine-react';

import {
    CreateSiteDialog,
    getSiteNotPublishableReason,
    SiteContentIcon,
    UpgradeDialogProvider,
    useSitePublish,
} from '../../Components';
import { SiteSortBy, useSortSites } from '../../Components/Sites';
import { getSitePermissions } from '../../state';
import { useGetSiteListPaletteGroups } from './SettingsPalette';

/**
 * Pressable current-site summary that opens the site switcher palette.
 */
export function SiteSwitcherButton(props: {
    organization: api.Organization;
    organizationId: string;
    canCreateSite: boolean;
    organizationContent: api.OrganizationAllContent;
    site: api.Site;
    structure: api.SiteStructure | null;
}) {
    const { canCreateSite, organization, organizationContent, organizationId, site, structure } =
        props;
    const createSiteDialogTrigger = useDialogTriggerState();
    const getSiteListPaletteGroups = useGetSiteListPaletteGroups();
    const { sortedSites } = useSortSites({
        allSites: organizationContent.sites,
        organizationId,
        sortBy: SiteSortBy.Viewed,
    });

    const siteListPalette = () =>
        createPalette({
            groups: getSiteListPaletteGroups({
                currentSiteId: site.id,
                organizationContent: {
                    ...organizationContent,
                    sites: sortedSites.map(({ item }) => item),
                },
                organizationId,
                onCreateSite: canCreateSite ? createSiteDialogTrigger.open : undefined,
            }),
        });

    return (
        <>
            <div className="group/site-switcher relative w-full min-w-0 flex-1">
                <PaletteAtomButton
                    palette={siteListPalette}
                    className="peer/current-site group/sidebar-item -outline-offset-1 flex w-full min-w-0 flex-col gap-1.5 rounded bg-neutral-surface-subtle px-1.5 py-[0.3125rem] text-left text-base outline-1 outline-neutral hover:bg-neutral-ui-hover data-[pressed=true]:bg-neutral-ui-active data-[focus-visible]:outline"
                >
                    <div className="flex w-full min-w-0 flex-1 items-center gap-2">
                        <SiteContentIcon
                            site={site}
                            className="size-4 shrink-0"
                            outlined={false}
                            withBackground={false}
                        />
                        <div className="min-w-0 truncate text-ui-standout">{site.title}</div>
                        <Icon.ChevronDown className="ml-auto size-4 shrink-0 text-muted" />
                    </div>

                    {site.urls.published ? (
                        <div className="flex w-full min-w-0 items-center justify-between gap-2 pr-7 text-muted">
                            <Tooltip label={site.urls.published}>
                                <span className="min-w-0 truncate text-ui-small">
                                    {site.urls.published}
                                </span>
                            </Tooltip>
                        </div>
                    ) : site.published && site.visibility === api.SiteVisibility.ShareLink ? (
                        <SidebarEmptyState className="mr-0 ml-0 w-full min-w-0 truncate pr-7">
                            Site published as share links
                        </SidebarEmptyState>
                    ) : !site.published ? (
                        <SidebarEmptyState className="mr-0 ml-0 w-full min-w-0 truncate pr-7">
                            This site is not published.
                        </SidebarEmptyState>
                    ) : null}
                </PaletteAtomButton>

                {site.urls.published ? (
                    <Button
                        {...currentSiteButtonProps}
                        href={site.urls.published}
                        target="_blank"
                        rel="noopener noreferrer"
                        label="Visit site"
                        leadingIcon={Icon.ExternalLink}
                    />
                ) : site.published && site.visibility === api.SiteVisibility.ShareLink ? (
                    <SiteSwitcherShareLinksButton
                        organization={organization}
                        organizationId={organizationId}
                        site={site}
                        structure={structure}
                    />
                ) : !site.published ? (
                    <UpgradeDialogProvider>
                        <SiteSwitcherPublishButton
                            organization={organization}
                            site={site}
                            structure={structure}
                        />
                    </UpgradeDialogProvider>
                ) : null}
            </div>

            {canCreateSite ? (
                <DialogRoot {...createSiteDialogTrigger.rootProps}>
                    <CreateSiteDialog organizationId={organizationId} />
                </DialogRoot>
            ) : null}
        </>
    );
}

const currentSiteButtonProps: Pick<ButtonProps, 'kind' | 'size' | 'iconOnly' | 'className'> = {
    kind: 'blank',
    size: 'small',
    iconOnly: true,
    className:
        'pointer-events-none absolute right-0.5 bottom-0.5 z-10 opacity-0 transition-opacity hover:pointer-events-auto hover:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover/site-switcher:pointer-events-auto group-hover/site-switcher:opacity-100 peer-focus-visible/current-site:pointer-events-auto peer-focus-visible/current-site:opacity-100 peer-data-[focus-visible]/current-site:pointer-events-auto peer-data-[focus-visible]/current-site:opacity-100',
};

function SiteSwitcherShareLinksButton(props: {
    organization: api.Organization;
    organizationId: string;
    site: api.Site;
    structure: api.SiteStructure | null;
}) {
    const { organization, organizationId, site, structure } = props;
    const canAdminSite = useCanAdminSite({ organization, structure });

    if (!canAdminSite) {
        return null;
    }

    return (
        <Button
            {...currentSiteButtonProps}
            href={{
                overlay: null,
                screen: {
                    type: 'site',
                    organization: organizationId,
                    site: site.id,
                    tab: {
                        type: 'settings',
                        section: 'audience',
                    },
                },
            }}
            label="Manage share links"
            leadingIcon={Icon.PublishStatusShareLink}
        />
    );
}

function SiteSwitcherPublishButton(props: {
    organization: api.Organization;
    site: api.Site;
    structure: api.SiteStructure | null;
}) {
    const { organization, site, structure } = props;
    const publish = useSitePublish({ organization });
    const canAdminSite = useCanAdminSite({ organization, structure });

    if (!canAdminSite) {
        return null;
    }

    const notPublishableReason = getSiteNotPublishableReason({ site, canAdminSite });

    return (
        <DialogRoot {...publish.dialogRootProps}>
            <Button
                {...currentSiteButtonProps}
                label="Publish"
                leadingIcon={Icon.Publish}
                disabled={Boolean(notPublishableReason)}
                title={notPublishableReason ?? undefined}
                loadable
                onPress={async () => {
                    await publish.publishSite(site);
                }}
            />
            {publish.publishDialogElement}
        </DialogRoot>
    );
}

function useCanAdminSite(props: {
    organization: api.Organization;
    structure: api.SiteStructure | null;
}) {
    const { organization, structure } = props;

    return React.useMemo(() => {
        if (!structure) {
            return false;
        }

        return getSitePermissions({ siteStructure: structure, organization }).canAdminSite;
    }, [organization, structure]);
}
